import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

import '../brand.css';

/**
 * Module-level, not a ref: demora's Router swaps the whole route component
 * on navigation, so this Layout unmounts and remounts every time. A ref
 * would reset with it and the guard below would never fire. This flag
 * survives, so the first paint after a real page load stays quiet and
 * every client-side navigation is announced.
 */
let hasNavigated = false;

/**
 * Client-side routing replaces the page without a browser navigation, so
 * this layout restores the two things a real page load would have done:
 * reset scroll, and tell assistive tech the page changed. Focus is moved
 * to the new <main> so keyboard users don't restart from the top of the
 * document on every link.
 */
export default function Layout({ children }) {
  const location = useLocation();
  const mainRef = useRef(null);
  const [announcement, setAnnouncement] = useState('');

  /**
   * `vm-site` scopes brand.css's base rules to the marketing pages — the
   * app shares one document with demora now, so those rules would
   * otherwise restyle /login and /admin. `hide-header`/`hide-footer` drop
   * demora's app chrome, since these pages bring their own.
   *
   * Done here rather than through config.hideHeader/hideFooter because
   * that would mean keeping a second copy of the marketing path list, and
   * it would not cover the catch-all 404. demora's App clears those two
   * classes on every navigation (in getDerivedStateFromProps, before
   * render), so re-adding them in a layout effect lands after that and
   * still beats the next paint.
   */
  useLayoutEffect(() => {
    const { classList } = document.body;

    classList.add('vm-site', 'hide-header', 'hide-footer');

    return () => classList.remove('vm-site', 'hide-header', 'hide-footer');
  }, []);

  useEffect(() => {
    if (!hasNavigated) {
      hasNavigated = true;
      return undefined;
    }

    window.scrollTo(0, 0);
    mainRef.current?.focus();

    // Let the new page's usePageMeta run before reading the title.
    const timer = window.setTimeout(() => {
      setAnnouncement(`${document.title} — page loaded`);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <a className="vm-skip-link" href="#main">
        Skip to main content
      </a>

      <Header />

      <main id="main" ref={mainRef} tabIndex={-1}>
        {children}
      </main>

      <Footer />

      <p aria-live="polite" role="status" className="vm-visually-hidden">
        {announcement}
      </p>
    </>
  );
}
